// React Icons imports - For pillars only (outline style)
import { HiOutlineLockClosed, HiOutlineCog6Tooth, HiOutlineClock } from "react-icons/hi2";
import type { IconType } from "react-icons";
import type { Case } from "./types";

// SVG imports for services (from assets)
import BrainCircuitIcon from "@/assets/Brain Circuit.svg";
import EyeIcon from "@/assets/EyeIcon.svg";
import RepoBranchIcon from "@/assets/RepoBranch.svg";
import FlashCircuitIcon from "@/assets/FlashCircuit.svg";
import AlvoIcon from "@/assets/Alvo.svg";
import DataIcon from "@/assets/DataIcon.svg";

export type Feature = {
  title: string;
  description: string;
  icon: string;
};

export type Pillar = {
  label: string;
  icon: IconType;
};

export type Benefit = {
  title: string;
  description: string;
};



export const pillars: Pillar[] = [
  { label: "Dados 100% seguros", icon: HiOutlineLockClosed },
  { label: "Modelos customizados", icon: HiOutlineCog6Tooth },
  { label: "Suporte especializado", icon: HiOutlineClock },
];

export const services: Feature[] = [
  {
    title: "Machine Learning",
    description: "Modelos preditivos personalizados que aprendem com seus dados e otimizam decisões automaticamente.",
    icon: BrainCircuitIcon,
  },
  {
    title: "Visão Computacional",
    description: "Análise e interpretação de imagens e vídeos para automação industrial, segurança e controle de qualidade.",
    icon: EyeIcon,
  },
  {
    title: "Agentes de IA",
    description: "Assistentes inteligentes que automatizam tarefas, conversam com usuários e integram sistemas de ponta a ponta.",
    icon: RepoBranchIcon,
  },
  {
    title: "IA Generativa",
    description: "Criação de conteúdo, código e soluções criativas utilizando modelos e dados moldados ao seu negócio.",
    icon: FlashCircuitIcon,
  },
  {
    title: "Automação Inteligente",
    description: "Workflows que combinam IA com processos existentes para máxima eficiência operacional.",
    icon: AlvoIcon,
  },
  {
    title: "Data Analytics",
    description: "Insights profundos através de análise avançada de dados e dashboards interativos em tempo real.",
    icon: DataIcon,
  },
];

export const consultingBenefits: Benefit[] = [
  {
    title: "Produtividade",
    description: "Automatize tarefas repetitivas e libere sua equipe para focar no que realmente importa: estratégia e inovação.",
  },
  {
    title: "Redução de Custos",
    description: "Otimize processos operacionais e diminua desperdícios com decisões baseadas em dados precisos.",
  },
  {
    title: "Tomada de Decisão",
    description: "Elimine a incerteza com insights preditivos e análises em tempo real sobre o seu negócio.",
  },
];



export const cases: Case[] = [
  // ── Visão Computacional ──────────────────────────────────────────────────
  {
    id: "alpr",
    title: "ALPR para Segurança Condominial",
    category: "cv",
    result: "Sistema processando mais de 100 mil placas por dia com controle automatizado de acesso e alertas em tempo real.",
    scale: "100k+",
    scaleSuffix: "placas/dia",
    tech: ["Python", "OpenALPR", "MariaDB", "Telegram Bot", "API REST"],
    featured: true,
  },
  {
    id: "antifurto",
    title: "Detecção de Furtos em Supermercados",
    category: "cv",
    result: "Plataforma validada com 55+ câmeras simultâneas, com agentes multimodais analisando comportamentos suspeitos em tempo real.",
    scale: "55+",
    scaleSuffix: "câmeras simultâneas",
    tech: ["YOLOv11", "NVIDIA Triton", "Kubernetes", "gRPC", "LLM"],
    featured: true,
  },
  {
    id: "aves-lidar",
    title: "Contagem de Aves e Estimativa de Massa",
    category: "cv",
    result: "Sistema híbrido com 18 câmeras e 2 sensores LiDAR para contagem e estimativa de massa em frigoríficos em tempo real.",
    scale: "18",
    scaleSuffix: "câmeras + 2 LiDAR",
    tech: ["Python", "C++", "YOLOv4", "PCL", "Kubernetes"],
    featured: false,
  },
  {
    id: "gaze",
    title: "Gaze Estimation em Farmácias",
    category: "cv",
    result: "Heatmaps de atenção visual por produto em gôndolas, apoiando decisões de merchandising e posicionamento de prateleira.",
    tech: ["Python", "OpenCV", "Gaze Estimation"],
    featured: false,
  },
  {
    id: "fadiga",
    title: "Monitoramento de Fadiga em Motoristas",
    category: "cv",
    result: "Detecção em tempo real de fadiga e distração com alertas sonoros em sistema embarcado em NVIDIA Jetson.",
    tech: ["Python", "OpenCV", "ResNet", "NVIDIA Jetson"],
    featured: false,
  },
  {
    id: "rastreamento",
    title: "Rastreamento e Re-Identificação de Pessoas",
    category: "cv",
    result: "Aplicado em 3 contextos: monitoramento fabril, Rio Web Summit e rede de farmácias, com re-identificação entre câmeras.",
    scale: "3",
    scaleSuffix: "contextos de uso",
    tech: ["Python", "OpenCV", "Autoencoder", "Filtro de Kalman", "GCP"],
    featured: false,
  },
  // ── Agentes de IA ────────────────────────────────────────────────────────
  {
    id: "multiagentes",
    title: "Plataforma Multiagentes como Core de IA",
    category: "agents",
    result: "Arquitetada e implementada ~80% solo. Plataforma cloud-agnóstica com catálogo de agentes, SDK de integração, A2A, MCP e observabilidade completa.",
    tech: ["Python", "Kubernetes", "LiteLLM", "LangFuse", "OpenTelemetry"],
    featured: true,
  },
  {
    id: "rag-medico",
    title: "Plataforma RAG para Médicos",
    category: "agents",
    result: "Busca semântica em grande volume de documentos, recuperação de imagens médicas por contexto e agentes com raciocínio observável.",
    tech: ["LangGraph", "LangChain", "PgVector", "PostgreSQL", "Docker"],
    featured: false,
  },
  {
    id: "text-to-sql",
    title: "Agente Text-to-SQL para Executivos",
    category: "agents",
    result: "Executivos com acesso autônomo a dados via voz ou texto diretamente no Microsoft Teams, com geração de gráficos e insights.",
    tech: ["LangChain", "Databricks", "Azure", "Whisper", "Teams Bot"],
    featured: false,
  },
  {
    id: "seguro-whatsapp",
    title: "Agente de Vendas de Seguros via WhatsApp",
    category: "agents",
    result: "Pipeline end-to-end de vendas e suporte de seguros via WhatsApp, da abordagem à contratação. Implantado na Argentina.",
    tech: ["LangChain", "LLM", "WhatsApp API", "Python"],
    featured: false,
  },
  {
    id: "carcacas",
    title: "Plataforma de Avaliação de Carcaças Bovinas",
    category: "agents",
    result: "Plataforma de avaliação automatizada em frigoríficos, com infraestrutura AWS robusta e stack de observabilidade com Grafana.",
    tech: ["Python", "OpenCV", "AWS ECS", "Grafana", "Prometheus"],
    featured: false,
  },
  // ── MLOps / LLMOps ───────────────────────────────────────────────────────
  {
    id: "llmops-databricks",
    title: "Pipeline LLMOps via Databricks",
    category: "mlops",
    result: "Novo modelo operativo adotado pela equipe no Peru. Case reconhecido pela Databricks como arquitetura de referência em LLMOps.",
    tech: ["Databricks", "MLflow", "AKS", "Azure", "GitHub Actions"],
    featured: true,
  },
  {
    id: "streaming-aws",
    title: "Perfilamento de Usuários de Streaming",
    category: "mlops",
    result: "Perfilamento detalhado com agentes de IA para recomendação personalizada. Reconhecido pela AWS como arquitetura agentizada de referência.",
    tech: ["AWS Glue", "Spark", "Mixtral 8b", "EC2", "LangChain"],
    featured: false,
  },
  {
    id: "mlops-pragas",
    title: "Plataforma MLOps para Detecção de Pragas",
    category: "mlops",
    result: "Cliente com autonomia para rotular dados, treinar e iterar seus próprios modelos com georeferenciamento das armadilhas.",
    tech: ["YOLOv4", "Streamlit", "GCP", "Cloud Storage", "Pipeline MLOps"],
    featured: false,
  },
  {
    id: "pipeline-veicular",
    title: "Pipeline de Imagens Veiculares Serverless",
    category: "mlops",
    result: "Pipeline de referência para substituição de background veicular com geração de vídeos publicitários e controle de qualidade via IA.",
    tech: ["IS-NET", "EfficientNet", "TFLite", "AWS Lambda", "Python"],
    featured: false,
  },
];
